// This file is auto-generated by @hey-api/openapi-ts

import { type DefaultError, queryOptions, type UseMutationOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { client } from '../client.gen';
import { carriersGetCarriers, compsTableGetCompsTable, compsVisibilityGetCompsVisibility, compsVisibilityUpdateCompsVisibility, configurationsCreateConfiguration, configurationsDeleteConfiguration, configurationsGetConfiguration, configurationsGetRfpConfiguration, configurationsListBrokerConfigurations, configurationsListBrokerMarketingPresentationTemplates, configurationsListConfigurations, configurationsListMarketingPresentationTemplateCatalog, configurationsPublishConfiguration, configurationsSetRfpConfiguration, configurationsSyncBrokerAssignments, configurationsUnpublishConfiguration, configurationsUpdateConfiguration, type Options, responsesTableGetResponsesTable, responsesTableUpdateResponsesTableCell, responsesVisibilityGetResponsesVisibility, responsesVisibilityUpdateResponsesVisibility, rumiGetLatestRumiRun, rumiStartRumiRun } from '../sdk.gen';
import type { CarriersGetCarriersData, CarriersGetCarriersError, CarriersGetCarriersResponse, CompsTableGetCompsTableData, CompsTableGetCompsTableResponse, CompsVisibilityGetCompsVisibilityData, CompsVisibilityGetCompsVisibilityResponse, CompsVisibilityUpdateCompsVisibilityData, CompsVisibilityUpdateCompsVisibilityResponse, ConfigurationsCreateConfigurationData, ConfigurationsCreateConfigurationResponse, ConfigurationsDeleteConfigurationData, ConfigurationsDeleteConfigurationResponse, ConfigurationsGetConfigurationData, ConfigurationsGetConfigurationResponse, ConfigurationsGetRfpConfigurationData, ConfigurationsGetRfpConfigurationResponse, ConfigurationsListBrokerConfigurationsData, ConfigurationsListBrokerConfigurationsResponse, ConfigurationsListBrokerMarketingPresentationTemplatesData, ConfigurationsListBrokerMarketingPresentationTemplatesResponse, ConfigurationsListConfigurationsData, ConfigurationsListConfigurationsResponse2, ConfigurationsListMarketingPresentationTemplateCatalogData, ConfigurationsListMarketingPresentationTemplateCatalogResponse, ConfigurationsPublishConfigurationData, ConfigurationsPublishConfigurationResponse, ConfigurationsSetRfpConfigurationData, ConfigurationsSetRfpConfigurationResponse, ConfigurationsSyncBrokerAssignmentsData, ConfigurationsSyncBrokerAssignmentsResponse, ConfigurationsUnpublishConfigurationData, ConfigurationsUnpublishConfigurationResponse, ConfigurationsUpdateConfigurationData, ConfigurationsUpdateConfigurationResponse, ResponsesTableGetResponsesTableData, ResponsesTableGetResponsesTableResponse, ResponsesTableUpdateResponsesTableCellData, ResponsesTableUpdateResponsesTableCellResponse, ResponsesVisibilityGetResponsesVisibilityData, ResponsesVisibilityGetResponsesVisibilityResponse, ResponsesVisibilityUpdateResponsesVisibilityData, ResponsesVisibilityUpdateResponsesVisibilityResponse, RumiGetLatestRumiRunData, RumiGetLatestRumiRunError, RumiGetLatestRumiRunResponse, RumiStartRumiRunData, RumiStartRumiRunError, RumiStartRumiRunResponse } from '../types.gen';

export type QueryKey<TOptions extends Options> = [
    Pick<TOptions, 'baseURL' | 'body' | 'headers' | 'path' | 'query'> & {
        _id: string;
        _infinite?: boolean;
        tags?: ReadonlyArray<string>;
    }
];

const createQueryKey = <TOptions extends Options>(id: string, options?: TOptions, infinite?: boolean, tags?: ReadonlyArray<string>): [
    QueryKey<TOptions>[0]
] => {
    const params: QueryKey<TOptions>[0] = { _id: id, baseURL: options?.baseURL || (options?.client ?? client).getConfig().baseURL } as QueryKey<TOptions>[0];
    if (infinite) {
        params._infinite = infinite;
    }
    if (tags) {
        params.tags = tags;
    }
    if (options?.body) {
        params.body = options.body;
    }
    if (options?.headers) {
        params.headers = options.headers;
    }
    if (options?.path) {
        params.path = options.path;
    }
    if (options?.query) {
        params.query = options.query;
    }
    return [params];
};

export const responsesTableGetResponsesTableQueryKey = (options: Options<ResponsesTableGetResponsesTableData>) => createQueryKey('responsesTableGetResponsesTable', options, false, ['Responses']);

/**
 * Get the Responses table for an RFP
 *
 * Returns the carriers × coverages matrix that powers the
 * Responses sub-tab.
 *
 * The response contains three top-level collections:
 * - `carriers` — one entry per carrier invited to the RFP, in
 * display sequence.
 * - `coverages` — one entry per coverage requested on the RFP,
 * in display sequence.
 * - `cells` — a **sparse** list of `(carrierId, planGroupId)`
 * pairs with the latest proposal data for that pair.
 *
 * Cells are sparse: any `(carrierId, planGroupId)` pair that
 * is *not* present in `cells[]` should be rendered as
 * `pending` by the client. This keeps payload size small for
 * early-stage RFPs where most carriers have not yet responded.
 *
 * Returns `200` with empty `carriers`/`coverages` arrays only
 * when the RFP exists but has no invited carriers and no
 * requested coverages. Otherwise the populated arrays describe
 * the grid the FE should render.
 *
 */
export const responsesTableGetResponsesTableOptions = (options: Options<ResponsesTableGetResponsesTableData>) => queryOptions<ResponsesTableGetResponsesTableResponse, AxiosError<DefaultError>, ResponsesTableGetResponsesTableResponse, ReturnType<typeof responsesTableGetResponsesTableQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await responsesTableGetResponsesTable({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: responsesTableGetResponsesTableQueryKey(options)
});

/**
 * Set the manual status for a single carrier × plan-group cell
 *
 * Allows an analyst to manually override the status displayed for a
 * specific `(carrierId, planGroupId)` cell. The `manualStatus` value
 * takes precedence over the status computed from the underlying
 * proposal data.
 *
 */
export const responsesTableUpdateResponsesTableCellMutation = (options?: Partial<Options<ResponsesTableUpdateResponsesTableCellData>>): UseMutationOptions<ResponsesTableUpdateResponsesTableCellResponse, AxiosError<DefaultError>, Options<ResponsesTableUpdateResponsesTableCellData>> => {
    const mutationOptions: UseMutationOptions<ResponsesTableUpdateResponsesTableCellResponse, AxiosError<DefaultError>, Options<ResponsesTableUpdateResponsesTableCellData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await responsesTableUpdateResponsesTableCell({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const responsesVisibilityGetResponsesVisibilityQueryKey = (options: Options<ResponsesVisibilityGetResponsesVisibilityData>) => createQueryKey('responsesVisibilityGetResponsesVisibility', options, false, ['Responses Visibility']);

/**
 * Get the shared hidden carriers and coverages for an RFP
 *
 * Returns the shared, per-RFP set of hidden carrier ids and hidden
 * plan-group ids. Returns `200` with empty arrays when no visibility
 * has been persisted yet.
 *
 */
export const responsesVisibilityGetResponsesVisibilityOptions = (options: Options<ResponsesVisibilityGetResponsesVisibilityData>) => queryOptions<ResponsesVisibilityGetResponsesVisibilityResponse, AxiosError<DefaultError>, ResponsesVisibilityGetResponsesVisibilityResponse, ReturnType<typeof responsesVisibilityGetResponsesVisibilityQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await responsesVisibilityGetResponsesVisibility({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: responsesVisibilityGetResponsesVisibilityQueryKey(options)
});

/**
 * Replace the shared hidden carriers and coverages for an RFP
 *
 * Upserts the shared, per-RFP hidden set. Each field, when present,
 * replaces the stored list wholesale; send an empty array to clear a
 * list. Omitted fields are left unchanged.
 *
 */
export const responsesVisibilityUpdateResponsesVisibilityMutation = (options?: Partial<Options<ResponsesVisibilityUpdateResponsesVisibilityData>>): UseMutationOptions<ResponsesVisibilityUpdateResponsesVisibilityResponse, AxiosError<DefaultError>, Options<ResponsesVisibilityUpdateResponsesVisibilityData>> => {
    const mutationOptions: UseMutationOptions<ResponsesVisibilityUpdateResponsesVisibilityResponse, AxiosError<DefaultError>, Options<ResponsesVisibilityUpdateResponsesVisibilityData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await responsesVisibilityUpdateResponsesVisibility({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const compsTableGetCompsTableQueryKey = (options: Options<CompsTableGetCompsTableData>) => createQueryKey('compsTableGetCompsTable', options, false, ['centro-portal', 'comps-table']);

/**
 * Get the Comps table for an RFP
 *
 * Returns the coverages × commission-columns matrix that
 * powers the Comps sub-tab of the *Present* tab.
 *
 * The response contains the following top-level collections:
 * - `coverages` — one entry per plan-group row: one per RFP
 * benefit plan group, plus one per off-script quoted proposal
 * plan group (rows).
 * - `carriers` — one entry per carrier the RFP was sent to,
 * in display sequence (carrier columns).
 * - `requestedColumn` — the always-present *Requested
 * Commission* column with one cell per coverage
 * (FR-PCP-01-04).
 * - `incumbentColumn` — present only when the RFP has an
 * incumbent carrier and incumbent commission data is
 * available; otherwise omitted.
 * - `cells` — **sparse** list of `(carrierId, planGroupId)`
 * carrier commission cells. Missing cells
 * should be rendered as `pending`.
 * - `notesRow` — the always-present bottom *Notes* row; its
 * internal `cells` array is sparse and may be empty.
 * - `subsidyRows` — optional fixed set of subsidy / credit
 * rows; absent or empty when not in use.
 *
 * Cells are sparse: any `(carrierId, planGroupId)` pair that
 * is *not* present in `cells[]` should be rendered as
 * `pending` by the client. This keeps payload size small for
 * early-stage RFPs where most carriers have not yet responded.
 *
 * Returns `200` with empty `coverages`/`carriers` arrays
 * only when the RFP exists but has no requested coverages
 * and no invited carriers. Otherwise the populated arrays
 * describe the grid the FE should render.
 *
 */
export const compsTableGetCompsTableOptions = (options: Options<CompsTableGetCompsTableData>) => queryOptions<CompsTableGetCompsTableResponse, AxiosError<DefaultError>, CompsTableGetCompsTableResponse, ReturnType<typeof compsTableGetCompsTableQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await compsTableGetCompsTable({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: compsTableGetCompsTableQueryKey(options)
});

export const compsVisibilityGetCompsVisibilityQueryKey = (options: Options<CompsVisibilityGetCompsVisibilityData>) => createQueryKey('compsVisibilityGetCompsVisibility', options, false, ['Comps Visibility']);

/**
 * Get the shared hidden rows and columns for an RFP
 *
 * Returns the shared, per-RFP set of hidden row-group ids and hidden
 * column ids. Returns `200` with `configured: false` and empty arrays
 * when no visibility has been persisted yet.
 *
 */
export const compsVisibilityGetCompsVisibilityOptions = (options: Options<CompsVisibilityGetCompsVisibilityData>) => queryOptions<CompsVisibilityGetCompsVisibilityResponse, AxiosError<DefaultError>, CompsVisibilityGetCompsVisibilityResponse, ReturnType<typeof compsVisibilityGetCompsVisibilityQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await compsVisibilityGetCompsVisibility({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: compsVisibilityGetCompsVisibilityQueryKey(options)
});

/**
 * Replace the shared hidden rows and columns for an RFP
 *
 * Upserts the shared, per-RFP hidden set. Each field, when present,
 * replaces the stored value wholesale; send an empty array to clear a
 * list. Omitted fields are left unchanged.
 *
 */
export const compsVisibilityUpdateCompsVisibilityMutation = (options?: Partial<Options<CompsVisibilityUpdateCompsVisibilityData>>): UseMutationOptions<CompsVisibilityUpdateCompsVisibilityResponse, AxiosError<DefaultError>, Options<CompsVisibilityUpdateCompsVisibilityData>> => {
    const mutationOptions: UseMutationOptions<CompsVisibilityUpdateCompsVisibilityResponse, AxiosError<DefaultError>, Options<CompsVisibilityUpdateCompsVisibilityData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await compsVisibilityUpdateCompsVisibility({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const carriersGetCarriersQueryKey = (options?: Options<CarriersGetCarriersData>) => createQueryKey('carriersGetCarriers', options, false, ['Carriers']);

/**
 * List all carriers
 *
 * Returns every carrier from carriertracker, each with its stable
 * `shareIdentifier` (`id`) and display `name`. The list is unfiltered
 * and not RFP-scoped.
 *
 */
export const carriersGetCarriersOptions = (options?: Options<CarriersGetCarriersData>) => queryOptions<CarriersGetCarriersResponse, AxiosError<CarriersGetCarriersError>, CarriersGetCarriersResponse, ReturnType<typeof carriersGetCarriersQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await carriersGetCarriers({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: carriersGetCarriersQueryKey(options)
});

export const configurationsListConfigurationsQueryKey = (options?: Options<ConfigurationsListConfigurationsData>) => createQueryKey('configurationsListConfigurations', options, false, ['configurations']);

/**
 * List configurations
 */
export const configurationsListConfigurationsOptions = (options?: Options<ConfigurationsListConfigurationsData>) => queryOptions<ConfigurationsListConfigurationsResponse2, AxiosError<DefaultError>, ConfigurationsListConfigurationsResponse2, ReturnType<typeof configurationsListConfigurationsQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await configurationsListConfigurations({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: configurationsListConfigurationsQueryKey(options)
});

/**
 * Create a configuration
 */
export const configurationsCreateConfigurationMutation = (options?: Partial<Options<ConfigurationsCreateConfigurationData>>): UseMutationOptions<ConfigurationsCreateConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsCreateConfigurationData>> => {
    const mutationOptions: UseMutationOptions<ConfigurationsCreateConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsCreateConfigurationData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await configurationsCreateConfiguration({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Delete a configuration
 */
export const configurationsDeleteConfigurationMutation = (options?: Partial<Options<ConfigurationsDeleteConfigurationData>>): UseMutationOptions<ConfigurationsDeleteConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsDeleteConfigurationData>> => {
    const mutationOptions: UseMutationOptions<ConfigurationsDeleteConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsDeleteConfigurationData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await configurationsDeleteConfiguration({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const configurationsGetConfigurationQueryKey = (options: Options<ConfigurationsGetConfigurationData>) => createQueryKey('configurationsGetConfiguration', options, false, ['configurations']);

/**
 * Get a configuration
 */
export const configurationsGetConfigurationOptions = (options: Options<ConfigurationsGetConfigurationData>) => queryOptions<ConfigurationsGetConfigurationResponse, AxiosError<DefaultError>, ConfigurationsGetConfigurationResponse, ReturnType<typeof configurationsGetConfigurationQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await configurationsGetConfiguration({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: configurationsGetConfigurationQueryKey(options)
});

/**
 * Update a configuration
 *
 * Update a configuration's name and/or nested configuration payload
 * (field settings, the dollar-difference toggle, and marketing-presentation
 * styling). Invalid style colors are dropped and reported in `warnings`.
 *
 */
export const configurationsUpdateConfigurationMutation = (options?: Partial<Options<ConfigurationsUpdateConfigurationData>>): UseMutationOptions<ConfigurationsUpdateConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsUpdateConfigurationData>> => {
    const mutationOptions: UseMutationOptions<ConfigurationsUpdateConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsUpdateConfigurationData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await configurationsUpdateConfiguration({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Publish a configuration
 */
export const configurationsPublishConfigurationMutation = (options?: Partial<Options<ConfigurationsPublishConfigurationData>>): UseMutationOptions<ConfigurationsPublishConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsPublishConfigurationData>> => {
    const mutationOptions: UseMutationOptions<ConfigurationsPublishConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsPublishConfigurationData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await configurationsPublishConfiguration({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

/**
 * Unpublish a configuration
 */
export const configurationsUnpublishConfigurationMutation = (options?: Partial<Options<ConfigurationsUnpublishConfigurationData>>): UseMutationOptions<ConfigurationsUnpublishConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsUnpublishConfigurationData>> => {
    const mutationOptions: UseMutationOptions<ConfigurationsUnpublishConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsUnpublishConfigurationData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await configurationsUnpublishConfiguration({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const configurationsListMarketingPresentationTemplateCatalogQueryKey = (options?: Options<ConfigurationsListMarketingPresentationTemplateCatalogData>) => createQueryKey('configurationsListMarketingPresentationTemplateCatalog', options, false, ['configurations']);

/**
 * List every available marketing-presentation template
 *
 * The catalog of template files Chameleon can render with, read from its
 * flat template folder. Used by admins assigning templates to a
 * configuration; which templates a given broker may use is configuration
 * data, not folder layout.
 *
 */
export const configurationsListMarketingPresentationTemplateCatalogOptions = (options?: Options<ConfigurationsListMarketingPresentationTemplateCatalogData>) => queryOptions<ConfigurationsListMarketingPresentationTemplateCatalogResponse, AxiosError<DefaultError>, ConfigurationsListMarketingPresentationTemplateCatalogResponse, ReturnType<typeof configurationsListMarketingPresentationTemplateCatalogQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await configurationsListMarketingPresentationTemplateCatalog({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: configurationsListMarketingPresentationTemplateCatalogQueryKey(options)
});

export const configurationsGetRfpConfigurationQueryKey = (options: Options<ConfigurationsGetRfpConfigurationData>) => createQueryKey('configurationsGetRfpConfiguration', options, false, ['configurations']);

/**
 * Get the configuration an RFP is spread against
 *
 * Resolves the MP configuration for this RFP: the analyst's selection when
 * there is one, the published default otherwise. Always 200 — `source` says
 * which applied, and the full configuration travels with it so the pages can
 * render field settings without a second request.
 *
 */
export const configurationsGetRfpConfigurationOptions = (options: Options<ConfigurationsGetRfpConfigurationData>) => queryOptions<ConfigurationsGetRfpConfigurationResponse, AxiosError<DefaultError>, ConfigurationsGetRfpConfigurationResponse, ReturnType<typeof configurationsGetRfpConfigurationQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await configurationsGetRfpConfiguration({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: configurationsGetRfpConfigurationQueryKey(options)
});

/**
 * Select the configuration an RFP is spread against
 */
export const configurationsSetRfpConfigurationMutation = (options?: Partial<Options<ConfigurationsSetRfpConfigurationData>>): UseMutationOptions<ConfigurationsSetRfpConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsSetRfpConfigurationData>> => {
    const mutationOptions: UseMutationOptions<ConfigurationsSetRfpConfigurationResponse, AxiosError<DefaultError>, Options<ConfigurationsSetRfpConfigurationData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await configurationsSetRfpConfiguration({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const configurationsListBrokerConfigurationsQueryKey = (options: Options<ConfigurationsListBrokerConfigurationsData>) => createQueryKey('configurationsListBrokerConfigurations', options, false, ['configurations']);

/**
 * List the configurations a broker's RFPs may be spread against
 *
 * Every published configuration assigned to the broker, falling back to the
 * published default when it has none of its own. This is what the analyst's
 * configuration picker offers.
 *
 */
export const configurationsListBrokerConfigurationsOptions = (options: Options<ConfigurationsListBrokerConfigurationsData>) => queryOptions<ConfigurationsListBrokerConfigurationsResponse, AxiosError<DefaultError>, ConfigurationsListBrokerConfigurationsResponse, ReturnType<typeof configurationsListBrokerConfigurationsQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await configurationsListBrokerConfigurations({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: configurationsListBrokerConfigurationsQueryKey(options)
});

export const configurationsListBrokerMarketingPresentationTemplatesQueryKey = (options: Options<ConfigurationsListBrokerMarketingPresentationTemplatesData>) => createQueryKey('configurationsListBrokerMarketingPresentationTemplates', options, false, ['configurations']);

/**
 * List the templates a broker may export with
 *
 * Templates from the broker's active configuration — the published
 * configuration assigned to the broker, falling back to the published
 * default configuration. `templates` is empty only when no default
 * configuration is published.
 *
 */
export const configurationsListBrokerMarketingPresentationTemplatesOptions = (options: Options<ConfigurationsListBrokerMarketingPresentationTemplatesData>) => queryOptions<ConfigurationsListBrokerMarketingPresentationTemplatesResponse, AxiosError<DefaultError>, ConfigurationsListBrokerMarketingPresentationTemplatesResponse, ReturnType<typeof configurationsListBrokerMarketingPresentationTemplatesQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await configurationsListBrokerMarketingPresentationTemplates({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: configurationsListBrokerMarketingPresentationTemplatesQueryKey(options)
});

/**
 * Sync broker assignments
 *
 * Atomically replaces all broker assignments for a configuration.
 */
export const configurationsSyncBrokerAssignmentsMutation = (options?: Partial<Options<ConfigurationsSyncBrokerAssignmentsData>>): UseMutationOptions<ConfigurationsSyncBrokerAssignmentsResponse, AxiosError<DefaultError>, Options<ConfigurationsSyncBrokerAssignmentsData>> => {
    const mutationOptions: UseMutationOptions<ConfigurationsSyncBrokerAssignmentsResponse, AxiosError<DefaultError>, Options<ConfigurationsSyncBrokerAssignmentsData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await configurationsSyncBrokerAssignments({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};

export const rumiGetLatestRumiRunQueryKey = (options: Options<RumiGetLatestRumiRunData>) => createQueryKey('rumiGetLatestRumiRun', options, false, [
    'rumi_centro-portal',
    'rfps',
    'rumi'
]);

/**
 * Get the RFP's latest Rumi Run
 *
 * Returns the RFP's most recent Run, open or ended, or `run: null` when
 * the RFP has never had one. While a Run is open, `outcome`, `endedAt`,
 * `rumiStatus`, `runCost`, and `results` are null. A Run that has just
 * started may not have a `seq` yet.
 *
 * For an ended Run, `results` holds what Rumi recorded, grouped by what
 * each statement is about, and every coverage the RFP has in btx with
 * each of its product and policy-period rows, their plan counts and
 * completion statuses. A Run that recorded nothing has empty groups,
 * not null ones. `results.counts` holds the numbers the Launchpad's
 * summary tiles show, counted from the RFP as it stands now.
 *
 */
export const rumiGetLatestRumiRunOptions = (options: Options<RumiGetLatestRumiRunData>) => queryOptions<RumiGetLatestRumiRunResponse, AxiosError<RumiGetLatestRumiRunError>, RumiGetLatestRumiRunResponse, ReturnType<typeof rumiGetLatestRumiRunQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await rumiGetLatestRumiRun({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: rumiGetLatestRumiRunQueryKey(options)
});

/**
 * Start a Rumi Run of the RFP
 *
 * Starts a Run and answers with it. `run` is null in the rare case
 * that the Run started but could not be read back; the next read of
 * `GET /rfps/{id}/rumi` returns it. Only one Run of an RFP may be open.
 *
 */
export const rumiStartRumiRunMutation = (options?: Partial<Options<RumiStartRumiRunData>>): UseMutationOptions<RumiStartRumiRunResponse, AxiosError<RumiStartRumiRunError>, Options<RumiStartRumiRunData>> => {
    const mutationOptions: UseMutationOptions<RumiStartRumiRunResponse, AxiosError<RumiStartRumiRunError>, Options<RumiStartRumiRunData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await rumiStartRumiRun({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};
